import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { Observable, Subscription, Subject, combineLatest } from "rxjs";
import { concatMap } from "rxjs/operators";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { RequestForScanning } from "../../../../common/models/scan-documents.model";
import { DocumentManagementService, IAzureBlobSasToken, IUploadFile } from "../../../../common/services/document-management.service";
import { WorkOrder } from "../../../../common/models/work-order.model";
import { WorkOrderService } from "../../../../common/services/work-order.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { FlowIndex } from "../../../../common/models/flow.dto";
import { SystemNotificationService } from "../../../../notifications/system-notifications/services/system-notification.service";
import { SystemNotification, SystemNotificationLevel } from "../../../../notifications/system-notifications/models/system-notification.model";
import { ScanRequestService } from "../../../services/scan-request.service";
import { AuthService } from "../../../../services/auth.service";
import { parseFileNameFromHeaders } from "../../../../common/models/onBaseIntegrationAndMapping.model";


@Component({
    selector: 'app-request-scan',
    templateUrl: './request-scan.component.html',
    styleUrls: ['./request-scan.component.scss'],
    standalone: false
})
export class RequestScanComponent implements OnInit, OnDestroy {

    cancelDialog: Dialog = new Dialog("Your request has been cancelled.", [new DialogButton("OK", false, () => { this.home(); })]);
    errorDialog: Dialog = new Dialog("An error has occurred", [new DialogButton("OK", false, () => { return; })]);
    submitDialog: Dialog = new Dialog("Request [Request#] has been created", [new DialogButton("OK", true, () => { this.home(); })]);

    coversheetBlob: any;
    @ViewChild("download", { static: true }) download: ElementRef;
    header: SubHeader = new SubHeader({ title: "Document Scanning", buttons: [] });

    subscription: Subscription = new Subscription();

    constructor(public router: Router, private _subheaderSvc: SubHeaderService, private _docService: DocumentManagementService, private loader : LoaderService, private _notificationService: SystemNotificationService,
        private _dialogService: DynamicDialogService, private _scanningService: ScanRequestService, private _workOrderService: WorkOrderService, public auth: AuthService) { }

    ngOnInit() {
        this._subheaderSvc.updateSubHeader(this.header);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    cancel(): void {        
        this.cancelDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.cancelDialog);
    }

    confirm(indicies: FlowIndex[]): void {
        let requestString = "";
        for (let i = 0; i < indicies.length; i++) {
            if (requestString.length > 0) requestString += ", ";
            requestString += `#${indicies[i].flowIdentifier}`;
        }
        this.submitDialog.message = this.submitDialog.message.replace("[Request#]", requestString);
        this.submitDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.submitDialog);
    }

    generateCoversheet(request: RequestForScanning): Observable<WorkOrder> {
        request.isAdhoc = true;
        return this._docService.generateScanningCover(request).pipe(concatMap((data) => {
            this.coversheetBlob = data.body;
            let file = new Blob([data.body], { type: "application/pdf" });
            let fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
            (file as any).name = fileName;
            (file as any).lastModifiedDate = new Date();

            return this._docService.provision("coversheet.pdf").pipe(concatMap((token: IAzureBlobSasToken) => {
                request.coversheetUrl = token.blobUri
                if (request.adhocRequestId == null || request.adhocRequestId.length === 0) {
                    let fileNameParts = fileName.split('.');
                    request.adhocRequestId = (fileNameParts.length > 0) ? fileNameParts[0] : "";
                }             
                request.isPaperApplication = false;
                this._docService.processProvisionedFile({ rawFile: <File>file, completed: false, isUploading: true, percentage: 0, fileName: "coversheet.pdf", type: "applicaiton/pdf", blobUri: token.blobUri } as IUploadFile, token, new Subject<IAzureBlobSasToken>())
                this.renderBlobs(file, fileName);
                return this._workOrderService.submitScanRequest(request);
            }));
        }));
    }

    handleError(error: any): void {
        this.errorDialog.toggleDialog(true);
        this._dialogService.updateDialog(this.errorDialog);
    }

    home(): void {
        this.router.navigate(["/home"]);
    }

    renderBlobs(data: any, fileName: string): void {
        const blob = new Blob([data], { type: "application/pdf " });
        let url = window.URL.createObjectURL(blob);
        const link = this.download.nativeElement;
        link.href = url;
        link.download = fileName;
        link.click();
    }

    requestsCreated(requests: RequestForScanning[]): void {        
        let obs$: Observable<WorkOrder>[] = [];
        requests.forEach(r => obs$.push(this.generateCoversheet(r)));
        this.loader.toggleLoader(true);
        let createdOrders: WorkOrder[];
        let createdIndexes: FlowIndex[];
        let submitSubs$ = combineLatest(...obs$).pipe(
            concatMap((orders: WorkOrder[]) => {
                createdOrders = orders;
                let reqObs$: Observable<FlowIndex>[] = [];
                orders.forEach(w => {
                    reqObs$.push(this._scanningService.createScanRequest(w));
                });
                return combineLatest(...reqObs$);
            }),
            concatMap((indicies: FlowIndex[]) => {
                createdIndexes = indicies;
                let notificationObs$: Observable<SystemNotification>[] = [];
                for (let i = 0; i < indicies.length; i++) {
                    let idx = indicies[i];
                    let notification: SystemNotification = {
                        flowId: idx.flowIdentifier,
                        orgId: this.auth.User.organizationId,
                        workOrderId: idx.currentWorkItemId,
                        redirectUrl: `/admin/scanning/request/${idx.currentWorkItemId}`,
                        level: SystemNotificationLevel.Information,
                        timestamp: new Date().toLocaleDateString(),
                        type: "Scanning",
                        groupId: createdOrders[i].assignedGroupId,
                        description: `Scanning Request ${idx.flowIdentifier} has been created.`
                    } as SystemNotification;
                    notificationObs$.push(this._notificationService.createNotification(notification));                
                }
                return combineLatest(...notificationObs$);
            })           
        ).subscribe(
            (notifications: SystemNotification[]) => {  
                this.loader.toggleLoader(false);
                this.confirm(createdIndexes);
            },
            (error) => {
                console.error(error);
                this.loader.toggleLoader(false);
                this.handleError(error);
            });
        this.subscription.add(submitSubs$);
    }
}
